import type { ChatMessage } from '../types/chat';
import {
  getAssistantMessageKind,
  shouldRenderFinalOnlyAssistantMessage,
} from './assistantMessage';

export interface FinalOnlyAssistantMessageInput {
  route: 'active' | 'subscribed';
  runId: string;
  content: string;
  gatewayId?: string;
  gatewayName?: string;
  agentLabel?: string;
  timestamp?: string;
  inputTokens?: number;
  outputTokens?: number;
}

/**
 * Inject a final-only assistant message into the UI list exactly once.
 *
 * The gateway can emit a final event without preceding deltas/tool-call events.
 * We still want to surface a real assistant message in that case, but we must
 * suppress protocol-only payloads and avoid duplicate injections when both the
 * active and subscribed-session paths observe the same run.
 */
export function injectFinalOnlyAssistantMessage(
  messages: ChatMessage[],
  input: FinalOnlyAssistantMessageInput,
): ChatMessage[] {
  const kind = getAssistantMessageKind(input.content);
  if (!shouldRenderFinalOnlyAssistantMessage(kind)) {
    return messages;
  }

  if (messages.some((message) => message.role === 'assistant' && message.runId === input.runId)) {
    return messages;
  }

  return [
    ...messages,
    {
      id: `inject-${input.route}-${input.runId}-${Date.now()}`,
      role: 'assistant',
      content: input.content,
      timestamp: input.timestamp ?? new Date().toISOString(),
      isHistory: false,
      isStreaming: false,
      ttsStatus: 'none',
      runId: input.runId,
      gatewayId: input.gatewayId,
      gatewayName: input.gatewayName,
      agentLabel: input.agentLabel,
      inputTokens: input.inputTokens,
      outputTokens: input.outputTokens,
    },
  ];
}
